// src\app\app.component.ts
import { Component, HostListener, inject, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Toast, ToastrService } from "ngx-toastr";
import { UserService } from "./services/user/user.service";
import { SessionService } from "./services/session/session.service";
import { MatSidenav } from "@angular/material/sidenav";
import { AuthService } from "./services/auth/auth.service";
import { User } from "./model/User";
import { WalletService } from "./services/wallet/wallet.service";
import { combineLatest, map } from "rxjs";

const REFRESH_CLICK_DELAY = 5000; // 5 seconds
const REFRESH_PRESS_DELAY = 500; // 0.5 seconds

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {
  /** Load services that need to load from startup */
  private _walletService: WalletService = inject(WalletService);

  /** Dependency injections */
  private _toastrService: ToastrService = inject(ToastrService);
  private _userService: UserService = inject(UserService);
  private _sessionService: SessionService = inject(SessionService);
  private _authService: AuthService = inject(AuthService);

  /** Indicates if the application has finished loading */
  public loading$ = combineLatest([
    this._authService.loading$,
    this._userService.loading$,
  ]).pipe(map(([authLoading, userLoading]) => authLoading || userLoading));

  /** Indicates user authenticated & active status */
  public active$ = combineLatest([
    this._authService.isAuthenticated$,
    this._userService.user$,
  ]).pipe(map(([isAuthenticated, user]) => isAuthenticated && user?.status === 'active'));

  /** Indicates user TOS acceptance status */
  public tosAccepted$ = this._userService.tosAccepted$;

  /** Sidenav */
  @ViewChild('sidenav') sidenav!: MatSidenav;
  public sidenavExpand = true;

  /**
   * Initalize toastr settings on construct
   */
  constructor() {
    // Set toastr config
    this._toastrService.toastrConfig.positionClass = 'toast-bottom-right';
    this._toastrService.toastrConfig.timeOut = 10 * 1000;
  }

  /**
   * Handle sidenav toggle
   */
  sidenavToggled(expanded: boolean) {
    this.sidenavExpand = expanded;
  }

  /**
   * Handle click events on the document to refresh session
   * 
   * @param event The mouse event
   */
  @HostListener('document:click', ['$event'])
  handleClickEvent(event: MouseEvent): void {
    if (this._authService.authenticated && !this._authService.isRefreshing) {
      setTimeout(() => this._sessionService.refresh(), REFRESH_CLICK_DELAY);
    }
  }

  /**
   * Handle keyboard events on the document to refresh session
   * 
   * @param event The keyboard event
   */
  @HostListener('document:keypress', ['$event'])
  handleKeyPressEvent(event: KeyboardEvent): void {
    if (this._authService.authenticated && !this._authService.isRefreshing) {
      setTimeout(() => this._sessionService.refresh(), REFRESH_PRESS_DELAY);
    }
  }
}
